/**
 * invoiceRuleActivity — pure parsing and wording for the invoice rule audit
 * trail: `invoice_rule.*` (invoice schedules) and `invoice_delivery_list.*`
 * (delivery lists).
 *
 * Kept free of React so the History tab, the activity detail view and the
 * tests all read a rule change the same way.
 *
 * Three metadata shapes reach this module and every one must render as plain
 * sentences, never raw data:
 *   - current rows:  { rule_kind, rule_name, action, changes{f:{old_value,new_value}},
 *                      settings{f:value}, origin{...} }
 *   - older schedule rows: the raw event payload — { rule_name, rule_type,
 *                      enabled, changes{f:{old_value,new_value}} } where
 *                      `locations` is a comma-joined list of ids and
 *                      `tag_names` a comma-joined list of names
 *   - older delivery-list rows: { changes{f:{new:true}} } — the field is known
 *                      to have changed, its values were never recorded
 */
// ─── Labels ──────────────────────────────────────────────────────────────
export const INVOICE_RULE_KIND_LABELS = {
    invoice_schedule: 'Invoice schedule',
    delivery_list: 'Delivery list',
};
// Same words as the rule editors (apps/web …/invoicing/schedules/constants.ts),
// so the history reads in the labels staff picked from. Copied, not imported:
// the shared package cannot depend on the web app.
const SCHEDULE_CADENCE = {
    immediate: 'Immediate (per work order close)',
    scheduled: 'Daily at a time',
    day_of_week: 'Weekly on a day',
    threshold: 'When N work orders accumulate',
};
const LIST_CADENCE = {
    immediate: 'Immediate (per invoice creation)',
    scheduled: 'Daily at a time',
    day_of_week: 'Weekly on a day',
};
const BUNDLE_MODES = {
    separate_emails: 'One invoice per email',
    bundled_pdfs_per_batch: 'One statement email, invoices attached separately',
    single_combined_pdf: 'One statement covering every invoice',
};
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
/** Display order; fields not listed here are shown after these, in name order. */
const FIELD_ORDER = [
    'name',
    'enabled',
    'cadence',
    'scheduled_time',
    'day_of_week',
    'threshold_count',
    'match_all_batches',
    'locations',
    'tag_names',
    'minimum_amount',
    'maximum_amount',
    'recipients',
    'suppress_delivery',
    'bundle_mode',
    'include_invoice_details',
    'priority',
];
/** Fields that carry no meaning for staff and are never shown. */
const HIDDEN_FIELDS = new Set(['split_by_location', 'account_id', 'rule_id', 'list_id', 'id']);
const LIST_FIELDS = new Set(['locations', 'tag_names', 'recipients']);
function fieldLabel(field, kind) {
    switch (field) {
        case 'name':
            return 'Name';
        case 'enabled':
            return 'Enabled';
        case 'cadence':
            return kind === 'delivery_list' ? 'When it sends' : 'When it runs';
        case 'scheduled_time':
            return 'Time of day';
        case 'day_of_week':
            return 'Day of the week';
        case 'threshold_count':
            return 'Work orders to wait for';
        case 'match_all_batches':
            return kind === 'delivery_list' ? 'Catches every invoice' : 'Every location in the account';
        case 'locations':
            return 'Locations';
        case 'tag_names':
            return 'Tags';
        case 'minimum_amount':
            return kind === 'delivery_list' ? 'Smallest invoice total' : 'Smallest work order total';
        case 'maximum_amount':
            return kind === 'delivery_list' ? 'Largest invoice total' : 'Largest work order total';
        case 'recipients':
            return 'Recipients';
        case 'suppress_delivery':
            return 'File without emailing anyone';
        case 'bundle_mode':
            return 'What the recipient gets';
        case 'include_invoice_details':
            return 'Include work order detail pages';
        case 'priority':
            return 'Order';
        default:
            return field.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
    }
}
// ─── Value formatting ───────────────────────────────────────────────────
const NOT_SET = 'Not set';
function isBlank(v) {
    return v === null || v === undefined || v === '';
}
function formatTime(hhmm) {
    const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
    if (!m)
        return hhmm;
    const h = Number(m[1]);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${m[2]} ${suffix}`;
}
function formatMoney(v) {
    const n = typeof v === 'number' ? v : Number(String(v));
    if (Number.isNaN(n))
        return String(v);
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Formats one scalar value for a field. */
export function formatRuleValue(field, value, kind) {
    if (isBlank(value))
        return NOT_SET;
    if (typeof value === 'boolean')
        return value ? 'Yes' : 'No';
    switch (field) {
        case 'cadence': {
            const map = kind === 'delivery_list' ? LIST_CADENCE : SCHEDULE_CADENCE;
            return map[String(value)] ?? String(value);
        }
        case 'scheduled_time':
            return formatTime(String(value));
        case 'day_of_week': {
            const n = Number(value);
            return DAYS[n] ?? String(value);
        }
        case 'minimum_amount':
        case 'maximum_amount':
            return formatMoney(value);
        case 'bundle_mode':
            return BUNDLE_MODES[String(value)] ?? String(value);
        case 'threshold_count':
            return `${value} work orders`;
        default:
            if (Array.isArray(value))
                return toList(field, value).join(', ') || 'None';
            if (typeof value === 'object')
                return 'Changed';
            return String(value);
    }
}
/**
 * Normalises a list-valued field. Arrays pass through; older rows stored
 * comma-joined strings. Location ids from older rows cannot be named here, so
 * they collapse to a count rather than showing raw ids.
 */
function toList(field, value) {
    let items;
    if (Array.isArray(value))
        items = value.map((v) => String(v));
    else if (isBlank(value))
        items = [];
    else
        items = String(value).split(',');
    items = items.map((s) => s.trim()).filter(Boolean);
    if (field === 'locations' && items.some((s) => UUID_RE.test(s))) {
        return items.length === 1 ? ['1 location'] : [`${items.length} locations`];
    }
    return items;
}
function listHasIds(value) {
    const raw = Array.isArray(value) ? value.map(String) : isBlank(value) ? [] : String(value).split(',');
    return raw.some((s) => UUID_RE.test(s.trim()));
}
// ─── Device / origin ─────────────────────────────────────────────────────
/**
 * Describes a browser user agent in plain words, e.g. "Chrome on Windows".
 * Returns undefined when there is nothing useful to say.
 */
export function describeDevice(userAgent) {
    if (!userAgent)
        return undefined;
    const ua = userAgent;
    if (/okhttp|CFNetwork|Darwin\/|ReactNative|Expo|turnconnect/i.test(ua) && !/Mozilla\/5\.0 \(/.test(ua)) {
        return 'TURN-CONNECT mobile app';
    }
    if (/curl|python-requests|Go-http-client|PostmanRuntime|axios|node-fetch/i.test(ua))
        return 'A connected system';
    let browser;
    if (/Edg\//.test(ua))
        browser = 'Edge';
    else if (/OPR\/|Opera/.test(ua))
        browser = 'Opera';
    else if (/Firefox\//.test(ua))
        browser = 'Firefox';
    else if (/Chrome\/|CriOS\//.test(ua))
        browser = 'Chrome';
    else if (/Safari\//.test(ua))
        browser = 'Safari';
    let os;
    if (/iPhone/.test(ua))
        os = 'iPhone';
    else if (/iPad/.test(ua))
        os = 'iPad';
    else if (/Android/.test(ua))
        os = 'Android';
    else if (/Windows/.test(ua))
        os = 'Windows';
    else if (/Mac OS X|Macintosh/.test(ua))
        os = 'Mac';
    else if (/CrOS/.test(ua))
        os = 'Chromebook';
    else if (/Linux/.test(ua))
        os = 'Linux';
    if (browser && os)
        return `${browser} on ${os}`;
    if (browser)
        return browser;
    if (os)
        return os;
    return 'Unknown device';
}
function str(v) {
    return typeof v === 'string' && v.trim() !== '' ? v : undefined;
}
function readOrigin(activity, metadata) {
    const o = (metadata.origin && typeof metadata.origin === 'object' ? metadata.origin : {});
    const person = str(activity.actor_name) ?? str(o.actor_name);
    const ua = str(activity.user_agent) ?? str(o.user_agent);
    const ip = str(activity.ip_address) ?? str(o.ip_address);
    // Older rows carry no origin; a person may still be named by the payload's
    // created_by / updated_by / deleted_by. Only call it automatic when nothing
    // anywhere names one.
    const legacyActor = str(metadata.created_by) ?? str(metadata.updated_by) ?? str(metadata.deleted_by);
    const automatic = o.source === 'system' || (!person && !activity.actor_id && !str(o.actor_id) && !legacyActor);
    return {
        person,
        location: str(o.session_location_name),
        device: automatic && !ua ? undefined : describeDevice(ua),
        ip,
        automatic,
    };
}
// ─── Parsing ─────────────────────────────────────────────────────────────
export function isInvoiceRuleActivityType(activityType) {
    return !!activityType && (activityType.startsWith('invoice_rule.') || activityType.startsWith('invoice_delivery_list.'));
}
function kindFor(activityType, metadata) {
    if (metadata.rule_kind === 'delivery_list' || metadata.rule_kind === 'invoice_schedule')
        return metadata.rule_kind;
    return activityType.startsWith('invoice_delivery_list.') ? 'delivery_list' : 'invoice_schedule';
}
function actionFor(activityType, metadata) {
    const a = metadata.action ?? activityType.split('.')[1];
    return a === 'created' || a === 'deleted' ? a : 'updated';
}
function orderFields(fields) {
    return [...fields].sort((a, b) => {
        const ia = FIELD_ORDER.indexOf(a);
        const ib = FIELD_ORDER.indexOf(b);
        if (ia === -1 && ib === -1)
            return a.localeCompare(b);
        if (ia === -1)
            return 1;
        if (ib === -1)
            return -1;
        return ia - ib;
    });
}
/** Reads one {old_value,new_value} / {old,new} / legacy {new:true} entry. */
function readPair(raw) {
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
        return { new: raw, hasOld: false, unknown: true };
    }
    const p = raw;
    if ('old_value' in p || 'new_value' in p) {
        return { old: p.old_value, new: p.new_value, hasOld: true, unknown: false };
    }
    // Older delivery-list rows wrap the change once more: {new: {old_value,new_value}}.
    if (p.new && typeof p.new === 'object' && !Array.isArray(p.new)) {
        return readPair(p.new);
    }
    if ('old' in p)
        return { old: p.old, new: p.new, hasOld: true, unknown: false };
    // {new: true} — the field changed, nothing else was recorded.
    return { new: p.new, hasOld: false, unknown: p.new === true || p.new === undefined };
}
function buildChangeRow(field, raw, kind) {
    const label = fieldLabel(field, kind);
    const pair = readPair(raw);
    if (pair.unknown)
        return { field, label, valuesUnknown: true };
    if (LIST_FIELDS.has(field)) {
        // Location ids cannot be diffed into names; say how many before and after.
        if (field === 'locations' && (listHasIds(pair.old) || listHasIds(pair.new))) {
            return { field, label, before: countLabel(toList(field, pair.old)), after: countLabel(toList(field, pair.new)) };
        }
        const before = toList(field, pair.old);
        const after = toList(field, pair.new);
        const beforeKeys = new Set(before.map((s) => s.toLowerCase()));
        const afterKeys = new Set(after.map((s) => s.toLowerCase()));
        return {
            field,
            label,
            added: after.filter((s) => !beforeKeys.has(s.toLowerCase())),
            removed: before.filter((s) => !afterKeys.has(s.toLowerCase())),
            before: before.length ? before.join(', ') : 'None',
            after: after.length ? after.join(', ') : 'None',
        };
    }
    return { field, label, before: formatRuleValue(field, pair.old, kind), after: formatRuleValue(field, pair.new, kind) };
}
function countLabel(items) {
    if (items.length === 0)
        return 'None';
    // toList already collapsed ids to "N locations".
    return items.join(', ');
}
function buildSettings(settings, kind) {
    return orderFields(Object.keys(settings).filter((f) => !HIDDEN_FIELDS.has(f))).map((field) => {
        const value = settings[field];
        const label = fieldLabel(field, kind);
        if (LIST_FIELDS.has(field)) {
            const items = toList(field, value);
            return { field, label, value: items.length ? items.join(', ') : 'None', items };
        }
        return { field, label, value: formatRuleValue(field, value, kind) };
    });
}
/** Older schedule create/delete rows carry a few settings at the top level. */
function legacySettings(metadata) {
    const out = {};
    if (typeof metadata.rule_type === 'string')
        out.cadence = metadata.rule_type;
    if (typeof metadata.cadence === 'string')
        out.cadence = metadata.cadence;
    if (typeof metadata.enabled === 'boolean')
        out.enabled = metadata.enabled;
    if (Array.isArray(metadata.location_names))
        out.locations = metadata.location_names;
    if (Array.isArray(metadata.tag_names))
        out.tag_names = metadata.tag_names;
    if (typeof metadata.recipient_count === 'number')
        out.recipients = `${metadata.recipient_count} recipient(s)`;
    return out;
}
export function parseInvoiceRuleActivity(activity) {
    const metadata = (activity.metadata ?? {});
    // Activities written through the generic recorder nest the payload.
    const data = !metadata.changes && !metadata.settings && metadata.original_data && typeof metadata.original_data === 'object'
        ? { ...metadata.original_data, origin: metadata.origin }
        : metadata;
    const kind = kindFor(activity.activity_type, data);
    const action = actionFor(activity.activity_type, data);
    const ruleName = str(data.rule_name) ?? str(data.name) ?? str(activity.resource_name);
    const rawChanges = data.changes && typeof data.changes === 'object' ? data.changes : {};
    const changes = orderFields(Object.keys(rawChanges).filter((f) => !HIDDEN_FIELDS.has(f))).map((f) => buildChangeRow(f, rawChanges[f], kind));
    let settings = [];
    if (action !== 'updated') {
        const raw = data.settings && typeof data.settings === 'object' ? data.settings : legacySettings(data);
        settings = buildSettings(raw, kind);
    }
    return {
        kind,
        kindLabel: INVOICE_RULE_KIND_LABELS[kind],
        action,
        ruleName,
        changes,
        settings,
        origin: readOrigin(activity, metadata),
    };
}
// ─── One-line summaries ──────────────────────────────────────────────────
/** A short sentence for a list row, e.g. "Changed Recipients and Time of day". */
export function summarizeInvoiceRuleActivity(parsed) {
    if (parsed.action === 'created')
        return `Created this ${parsed.kindLabel.toLowerCase()}`;
    if (parsed.action === 'deleted')
        return `Deleted this ${parsed.kindLabel.toLowerCase()}`;
    const enabled = parsed.changes.find((c) => c.field === 'enabled');
    if (enabled && parsed.changes.length === 1 && !enabled.valuesUnknown) {
        return enabled.after === 'Yes' ? 'Turned it on' : 'Turned it off';
    }
    const labels = parsed.changes.map((c) => c.label);
    if (labels.length === 0)
        return 'Saved without changes';
    if (labels.length === 1)
        return `Changed ${labels[0]}`;
    if (labels.length <= 3)
        return `Changed ${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
    return `Changed ${labels.slice(0, 2).join(', ')} and ${labels.length - 2} more`;
}
/** "Jane Doe · working in Downtown Kia · Chrome on Windows". */
export function describeOrigin(origin) {
    const parts = [];
    parts.push(origin.person ?? (origin.automatic ? 'System (automatic)' : 'Unknown person'));
    if (origin.location)
        parts.push(`working in ${origin.location}`);
    if (origin.device)
        parts.push(origin.device);
    return parts.join(' · ');
}
